import { Flex, Spacer, Skeleton, useToast } from '@chakra-ui/react';
import React, { useState } from 'react';

import { CoreBorrowingBaseInsightsGqlDataGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { PortfolioChartCards } from './PortfolioChartCards';
import { PortfolioChartWithCardsTemplateForCreditFacility } from './PortfolioChartTemplate';
import {
  chartsInitialEndDateJs,
  chartsInitialStartDateJs,
  ChartTimePeriod,
  CoreBorrowingBaseInsightsKeys,
  defaultSelectedTimePeriod,
  formatChartDataByReportingDate,
  formatCsvDownloadDataRows,
  getInitialActiveFields,
  onToggleActiveField,
  PortfolioChartConfig,
} from './portfolioChartUtils';
import { PortfolioDownloadCsvLink } from './PortfolioDownloadCsvLink';
import { PortfolioReChart } from './PortfolioReChart';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
  errorToastConfig,
} from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { useGetDailyCoreBorrowingBaseInsightsQuery } from '~/packages/portfolio/requests/requests.gql.generated';

type PortfolioCoreBorrowingBaseChartTemplateProps = {
  creditFacilityId: string;
  chartTitle: string;
  chartConfig: PortfolioChartConfig<any>;
};
export const PortfolioCoreBorrowingBaseChartTemplate = ({
  creditFacilityId,
  chartTitle,
  chartConfig,
}: PortfolioCoreBorrowingBaseChartTemplateProps) => {
  const toast = useToast();
  const initialActiveFields = getInitialActiveFields({
    chartConfig,
  }) as CoreBorrowingBaseInsightsKeys[];

  const [
    selectedBar,
    setSelectedBar,
  ] = useState<CoreBorrowingBaseInsightsGqlDataGQLType | null>(null);
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: chartsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [chartDataForDateRange, setChartDataForDateRange] = useState<
    CoreBorrowingBaseInsightsGqlDataGQLType[]
  >([]);
  const [chartDataWithTimePeriod, setChartDataWithTimePeriod] = useState<
    CoreBorrowingBaseInsightsGqlDataGQLType[]
  >([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    defaultSelectedTimePeriod,
  );

  const [activeFields, setActiveFields] = useState<
    CoreBorrowingBaseInsightsKeys[]
  >(initialActiveFields);

  const {
    refetch: refetchBorrowingBaseInsightsData,
    data: borrowingBaseInsightsData,
    loading: borrowingBaseInsightsLoading,
  } = useGetDailyCoreBorrowingBaseInsightsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
    onCompleted: (data) => {
      // we must make sure to set the core date range chart data with all of the data by day
      // this ensures that we have the base by-day data ready to use
      // when going back to a higher-frequency time period view than the currently selected period
      const formattedChartData = formatChartDataByReportingDate({
        chartData: data.getDailyCoreBorrowingBaseInsights,
        selectedTimePeriod: ChartTimePeriod.day,
      });
      setChartDataForDateRange(formattedChartData);

      // then set data for the specific time period
      if (selectedTimePeriod !== ChartTimePeriod.day) {
        const formattedChartDataForPeriod = formatChartDataByReportingDate({
          chartData: data.getDailyCoreBorrowingBaseInsights,
          selectedTimePeriod,
        });
        setChartDataWithTimePeriod(formattedChartDataForPeriod);
      } else {
        setChartDataWithTimePeriod(formattedChartData);
      }

      setSelectedBar(formattedChartData[formattedChartData.length - 1]);
    },
    notifyOnNetworkStatusChange: true,
  });

  if (borrowingBaseInsightsLoading || !borrowingBaseInsightsData) {
    return <Skeleton height="700px" />;
  }

  const onSelectTimePeriod = (timePeriod: ChartTimePeriod) => {
    setSelectedTimePeriod(timePeriod);

    const formattedChartData = formatChartDataByReportingDate({
      chartData: chartDataForDateRange,
      selectedTimePeriod: timePeriod,
    });

    setChartDataWithTimePeriod(formattedChartData);
  };

  const onSelectBar = (
    selectedBarData: CoreBorrowingBaseInsightsGqlDataGQLType,
  ) => {
    setSelectedBar(selectedBarData);
  };

  const {
    dataRows,
    headers,
  } = formatCsvDownloadDataRows<CoreBorrowingBaseInsightsGqlDataGQLType>(
    chartDataWithTimePeriod,
    activeFields,
  );

  return (
    <PortfolioChartWithCardsTemplateForCreditFacility
      creditFacilityId={creditFacilityId}
      chartTitle={chartTitle}
      refetch={refetchBorrowingBaseInsightsData}
      chartDataDateRange={chartDataDateRange}
      setChartDataDateRange={setChartDataDateRange}
      selectedTimePeriod={selectedTimePeriod}
      onSelectTimePeriod={onSelectTimePeriod}
    >
      {/* child 1 */}
      <PortfolioReChart
        chartData={chartDataWithTimePeriod}
        selectedTimePeriod={selectedTimePeriod}
        currentSelectedBarReportingDate={
          selectedBar
            ? selectedBar.reportingDate
            : getDefaultDashboardDisplayEndDate()
        }
        onSelectBar={onSelectBar}
        chartConfig={chartConfig}
        visibleChartComponents={activeFields}
        tooltipLabelType="date"
      />

      {/* child 2 */}
      {selectedBar && (
        <Flex width="25%" flexDirection="column">
          <PortfolioChartCards
            selectedBarData={selectedBar}
            chartConfig={chartConfig}
            activeFields={activeFields}
            onToggleActiveField={(selectedField) =>
              onToggleActiveField({
                selectedField,
                currentActiveFields: activeFields,
                setActiveFields,
              })
            }
          />
          <Spacer />

          <PortfolioDownloadCsvLink
            dataRows={dataRows}
            headers={headers}
            chartTitle={chartTitle}
            selectedTimePeriod={selectedTimePeriod}
          />
        </Flex>
      )}
    </PortfolioChartWithCardsTemplateForCreditFacility>
  );
};
